import { afterNextRender, ChangeDetectorRef, Component, computed, effect, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BasketApiMap } from 'src/business/basket';
import { ProductCardModelSpecificationGroupSearchProductResponse, SearchRequestModel } from 'src/business/openapi';
import { CommonModule } from 'src/common/common.module';
import { SocialLinksComponent } from 'src/common/components/social-links/social-links.component';
import { ConsentEvent, CustomNotificationEvent, FabEvent, NavigateEvent, ScrollEvent, ThemeChangeEvent } from 'src/common/events';
import { Link, MainConfiguration, MenuConfiguration, NotificationModel } from 'src/common/models';
import { EcommerceBasketStaticComponent } from 'src/ecommerce/components/ecommerce-basket-static/ecommerce-basket-static.component';
import { GridComponent } from 'src/layout/grid/grid.component';
import { ProductSearchBarComponent } from 'src/product/components/product-search-bar/product-search-bar.component';
import { ProductSearchListComponent } from 'src/product/components/product-search-list/product-search-list.component';
import { StoreContactComponent } from 'src/store/components/store-contact/store-contact.component';
import { StoreHoursOfOperationComponent } from 'src/store/components/store-hours-of-operation/store-hours-of-operation.component';
import { ContentCacheUtils } from 'src/utils/storage.util';
import { Uuid } from 'src/utils/uuid.util';
import { NavFragmentComponent } from './nav-fragment/nav-fragment.component';
import { NavListComponent } from './nav-list/nav-list.component';
import { origin } from 'src/utils/origin';
import { FabOutletComponent } from 'src/layout/fab-outlet/fab-outlet.component';
import { TranslateService } from '@ngx-translate/core';
import { EcommerceSummaryComponent } from "../ecommerce/components/ecommerce-summary/ecommerce-summary.component";
import { SearchApiMap } from 'src/business/search';
import { UrlUtil } from 'src/utils/url.util';
import { filter } from 'rxjs';
import { EcommercePaymentCardsComponent } from 'src/ecommerce/components/ecommerce-payment-cards/ecommerce-payment-cards.component';
import { EcommerceMarketSelectorComponent } from 'src/ecommerce/components/ecommerce-market-selector/ecommerce-market-selector.component';

type SearchState = null | {
  query: string
}
type BasketState = null | {
  quantity: number;
}
type LoginState = null | {
  name?: string;
  icon: 'lock' | 'lock-up'
}
class NotificationSequencer {

  constructor() {
  }

}
@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterModule,
    NavFragmentComponent,
    NavListComponent,
    GridComponent,
    EcommerceBasketStaticComponent,
    StoreContactComponent,
    StoreHoursOfOperationComponent,
    ProductSearchBarComponent,
    ProductSearchListComponent,
    SocialLinksComponent,
    FabOutletComponent,
    EcommerceSummaryComponent,
    EcommercePaymentCardsComponent,
    EcommerceMarketSelectorComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent {
  logoUrl: WritableSignal<{ url: string, srcset: string, sizes: string, link: string }>;
  menu?: MenuConfiguration | null;
  search: Signal<SearchState>;
  basket: Signal<BasketState>;
  login: Signal<LoginState>;
  basketUrl: string | undefined;
  notificationList: WritableSignal<NotificationModel[]>;
  productSearchRequest: WritableSignal<SearchRequestModel | undefined>;
  productSearchResult: Signal<Promise<ProductCardModelSpecificationGroupSearchProductResponse | null>>;
  searchLink: Signal<string | undefined>;

  constructor(private router: Router, private translateService: TranslateService, private _detector: ChangeDetectorRef) {
    this.logoUrl = signal<{ url: string, srcset: string, sizes: string, link: string }>({ url: '', srcset: '', sizes: '', link: '/' });
    this.notificationList = signal<NotificationModel[]>([]);
    this.productSearchRequest = signal<SearchRequestModel | undefined>(undefined);
    let _basket = toSignal(BasketApiMap.basket$)
    this.search = computed(() => {
      let query = '';
      return { query }
    })
    this.basket = computed(() => {
      let __basket = _basket();
      let quantity = __basket?.basket?.quantity ?? 0
      return { quantity }
    })
    this.login = computed(() => {
      let icon: 'lock' | 'lock-up' = 'lock-up';
      let name;
      return { icon, name }
    })
    this.productSearchResult = computed(async () => {
      let req = this.productSearchRequest();
      if (req && (req?.searchTerms?.length ?? 0) >= 2)
        return await SearchApiMap.searchClean(req)
      return Promise.resolve(null);
    });
    this.searchLink = computed(() => {
      let req = this.productSearchRequest();
      if (!req)
        return;
      let searchUrl = ContentCacheUtils.url('searchPage');
      if (!searchUrl)
        return;
      let url = new URL(searchUrl, globalThis.origin);
      url.searchParams.set(UrlUtil.SEARCH_PREFIX, req.searchTerms ?? '');
      return url.href;
    })
    let navigationEnd = toSignal(this.router.events.pipe(filter(e => e instanceof NavigationEnd)))

    effect(() => {
      navigationEnd();
      this.productSearchRequest.set(undefined);
    })

    afterNextRender(() => {
      CustomNotificationEvent.addEventListener(notification => this.addNotification(notification))
      let system = ContentCacheUtils.system<MainConfiguration>();
      this.menu = ContentCacheUtils.menu<MenuConfiguration>();
      AppComponent.forceIds(this.menu?.contentMenu);
      AppComponent.forceIds(this.menu?.productMenu);
      if (system) {
        let logoConfig = AppComponent.mapLogo(system)
        if (logoConfig)
          this.logoUrl.set(logoConfig);
      }
      ConsentEvent.cookieStore();
      ThemeChangeEvent.set(system);
      NavigateEvent.listen((link: string) => this.router.navigateByUrl(link))
      ScrollEvent.addEventListner(() => globalThis.document.body.style
        .setProperty('--scroll', (
          globalThis.scrollY / (
            globalThis.document.body.offsetHeight - globalThis.innerHeight
          )
        ) + '')
      )
      this.basketUrl = ContentCacheUtils.url('basketPage');
      setTimeout(() =>
        FabEvent.dispatch({
          id: 'scrollToTop',
          action: () => globalThis.document.documentElement.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
          }),
          svg: 'chevron-up',
          title: this.translateService.instant('FAB.SCROLL_TO_TOP'),
          style: {
            'background-color': 'var(--fab-scroll-to-top-background-color)',
            "color": 'var(--fab-scroll-to-top-color)',
            "border-radius": "50%",
            "order": "-1",
          }
        }), 2000);
      BasketApiMap.get();
    })
  }

  private addNotification(notification: NotificationModel) {
    let list = this.notificationList()
    list.push(notification);
    this.notificationList.set(list);
    this._detector.markForCheck();
  }

  searchSubmit(event: SearchRequestModel | undefined) {
    if (event == null)
      return
    let link = this.searchLink();
    if (link)
      this.router.navigateByUrl(link.replace(origin(), ''));
  }

  static mapLogo(system: MainConfiguration) {
    if (system?.theme?.logo?.imageUrl != null) {
      let _url = new URL(system.theme.logo.imageUrl, origin());
      let _mobileUrl = system?.theme?.logoMobile?.imageUrl == null ? _url : new URL(system.theme.logoMobile.imageUrl, origin());
      let url = _url.href;
      let fn = (__url: URL) => (s: number) => { __url.searchParams.set('height', s + ''); return `${__url.href} ${s}w` };
      let srcset = [[140, 70].map(s => fn(_url)(s)).join(', '), [100, 50].map(s => fn(_mobileUrl)(s)).join(', ')].join(', ');
      let sizes = _url.href.endsWith('.svg') ? '' : `((min-width:1000px) and (min-device-pixel-ratio: 1.01)) 140px, ((max-width:1000px) and (max-device-pixel-ratio: 1)) 70px, ((min-width:1000px) and (min-device-pixel-ratio: 1.01)) 100px, ((max-width:1000px) and (max-device-pixel-ratio: 1)) 50px`;
      let link = ContentCacheUtils.url('homePage') ?? '/';
      return { url, srcset, sizes, link };
    }
    return undefined;
  }

  private static forceIds(menu: Link[] | undefined) {
    if (!menu)
      return;
    menu.forEach(i => {
      i.id = i.id ?? 'menu-' + Uuid.create();
      if (i.children) {
        AppComponent.forceIds(i.children)
      }
    })
  }
}
